import React, { useState, useEffect } from 'react';
import { empresaService, grupoService } from '../../services/entitiesServices';
import type { Empresa, Grupo } from '../../types/entities';
import { toSaoPauloTimestamp } from '../../utils/formatters';

interface EmpresaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  empresa?: Empresa | null;
}

export const EmpresaModal: React.FC<EmpresaModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  empresa
}) => {
  const [formData, setFormData] = useState({
    nome: '',
    cnpj: '',
    email: '',
    telefone: '',
    validadeLicenca: '',
    grupoId: ''
  });
  const [grupos, setGrupos] = useState<Grupo[]>([]);
  const [loadingGrupos, setLoadingGrupos] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadGrupos();
      if (empresa) {
        setFormData({
          nome: empresa.nome,
          cnpj: empresa.cnpj,
          email: empresa.email,
          telefone: empresa.telefone || '',
          validadeLicenca: empresa.validadeLicenca ? new Date(empresa.validadeLicenca).toISOString().split('T')[0] : '',
          grupoId: empresa.grupoId || ''
        });
      } else {
        setFormData({
          nome: '',
          cnpj: '',
          email: '',
          telefone: '',
          validadeLicenca: '',
          grupoId: ''
        });
      }
      setError('');
    }
  }, [isOpen, empresa]);

  const loadGrupos = async () => {
    setLoadingGrupos(true);
    try {
      console.log('🔄 Carregando grupos para seleção...');
      const response = await grupoService.list();
      console.log('📡 Resposta da API de grupos:', response);
      
      // Tratamento mais robusto da resposta da API
      let gruposData: Grupo[] = [];
      
      if (Array.isArray(response)) {
        gruposData = response;
      } else if (response && (response as any).grupos) {
        gruposData = (response as any).grupos;
      } else if (response && response.data) {
        gruposData = response.data;
      } else if (response && typeof response === 'object') {
        // Caso a resposta seja um objeto com propriedades não padronizadas
        const possibleArrays = Object.values(response).filter(Array.isArray);
        if (possibleArrays.length > 0) {
          gruposData = possibleArrays[0] as Grupo[];
        }
      }
      
      console.log('📋 Grupos processados:', gruposData);
      setGrupos(gruposData);
    } catch (err) {
      console.error('❌ Erro ao carregar grupos:', err);
      setGrupos([]); // Garantir que grupos seja um array vazio em caso de erro
    } finally {
      setLoadingGrupos(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!formData.nome.trim() || !formData.cnpj.trim() || !formData.email.trim()) {
        throw new Error('Nome, CNPJ e email são obrigatórios');
      }

      const data = {
        nome: formData.nome.trim(),
        cnpj: formData.cnpj.replace(/\D/g, ''),
        email: formData.email.trim(),
        telefone: formData.telefone.trim() || undefined,
        situacao: 'ativo',
  validadeLicenca: formData.validadeLicenca ? toSaoPauloTimestamp(formData.validadeLicenca) : undefined,
        grupoId: formData.grupoId || undefined,
      };

      if (empresa) {
        await empresaService.update(empresa.id, data);
      } else {
        await empresaService.create(data);
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar empresa');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800">
            {empresa ? 'Editar Empresa' : 'Nova Empresa'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome da Empresa *
              </label>
              <input
                type="text"
                required
                value={formData.nome}
                onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Digite o nome da empresa"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CNPJ *
              </label>
              <input
                type="text"
                required
                value={formData.cnpj}
                onChange={(e) => setFormData(prev => ({ ...prev, cnpj: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="00.000.000/0000-00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="email@empresa.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telefone
              </label>
              <input
                type="text"
                value={formData.telefone}
                onChange={(e) => setFormData(prev => ({ ...prev, telefone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="(11) 99999-9999"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Validade da Licença
              </label>
              <input
                type="date"
                value={formData.validadeLicenca}
                onChange={(e) => setFormData(prev => ({ ...prev, validadeLicenca: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Grupo
              </label>
              <select
                value={formData.grupoId}
                onChange={(e) => setFormData(prev => ({ ...prev, grupoId: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loadingGrupos}
              >
                <option value="">
                  {loadingGrupos ? 'Carregando grupos...' : 'Selecione um grupo (opcional)'}
                </option>
                {!loadingGrupos && grupos.map((grupo) => (
                  <option key={grupo.id} value={grupo.id}>
                    {grupo.nome}
                  </option>
                ))}
              </select>
              {loadingGrupos && (
                <div className="flex items-center mt-1 text-sm text-gray-500">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Carregando grupos disponíveis...
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-2 px-4 rounded-lg font-medium hover:from-blue-700 hover:to-cyan-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Salvando...' : (empresa ? 'Atualizar' : 'Criar Empresa')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};